import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { 
  TrendingUp, 
  Users, 
  Package, 
  DollarSign,
  BarChart3
} from "lucide-react";

const COLORS = ['#0F172A', '#F97316', '#64748B', '#2563EB', '#10B981', '#8B5CF6', '#EC4899', '#14B8A6'];

export default function AnalyticsPage() {
  const [stats, setStats] = useState(null);
  const [ordersByStatus, setOrdersByStatus] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [statsRes, statusRes] = await Promise.all([
        api.get("/analytics/dashboard"),
        api.get("/analytics/orders-by-status")
      ]);
      setStats(statsRes.data);
      setOrdersByStatus(statusRes.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const statusLabels = {
    order_confirmed: "Confirmed",
    material_planning: "Planning",
    cutting_scheduled: "Scheduled",
    cutting_in_progress: "Cutting",
    qc_check: "QC Check",
    packed: "Packed",
    dispatched: "Dispatched",
    delivered: "Delivered"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="analytics-page">
      {/* Header */}
      <div className="border-b border-slate-200 pb-6">
        <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase tracking-tight" data-testid="analytics-title">
          Analytics
        </h1>
        <p className="text-slate-500 mt-1">Business insights and performance metrics</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="card-industrial" data-testid="kpi-revenue">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-green-600 flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">₹{((stats?.total_revenue || 0) / 1000).toFixed(1)}K</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-industrial" data-testid="kpi-orders">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-600 flex items-center justify-center">
                <Package className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{stats?.total_orders || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Total Orders</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-industrial" data-testid="kpi-pending">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-orange-500 flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{stats?.pending_orders || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-industrial" data-testid="kpi-customers">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-purple-600 flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{stats?.total_customers || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Customers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-industrial" data-testid="kpi-products">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-teal-600 flex items-center justify-center">
                <Package className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{stats?.total_products || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Products</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders by Status - Pie Chart */}
        <Card className="card-industrial">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="font-heading text-lg uppercase tracking-wider">
              Orders by Status
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {ordersByStatus.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>No order data available</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={ordersByStatus.map(item => ({
                      ...item,
                      name: statusLabels[item.status] || item.status
                    }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="name"
                    label={({ name, count }) => `${name}: ${count}`}
                  >
                    {ordersByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [value, name]}
                    contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 0 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Orders by Status - Bar Chart */}
        <Card className="card-industrial">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="font-heading text-lg uppercase tracking-wider">
              Order Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {ordersByStatus.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>No order data available</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ordersByStatus.map(item => ({
                  ...item,
                  name: statusLabels[item.status] || item.status
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [value, 'Orders']}
                    contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 0 }}
                  />
                  <Bar dataKey="count" fill="#0F172A" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
